import React from "react";
import TextField from "@mui/material/TextField";

const EMOJI_PATTERN = /\p{Extended_Pictographic}/u;
const NEGATIVE_INT_PATTERN = /-\d/;
const EMAIL_CHAR_PATTERN = /^[a-z0-9@._+-]*$/;
export const EMAIL_FORMAT_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function getEmailValidationError(value, { requireComplete = false } = {}) {
  const val = String(value ?? "").toLowerCase();
  if (!val) return null;
  if (!EMAIL_CHAR_PATTERN.test(val)) return "Enter a valid email address";
  if (requireComplete || val.includes("@")) {
    if (!EMAIL_FORMAT_PATTERN.test(val)) return "Enter a valid email address";
  }
  return null;
}

export function getUsernameValidationError(value) {
  const val = String(value ?? "");
  if (!val) return null;
  if (EMOJI_PATTERN.test(val)) return "Emojis are not allowed";
  if (NEGATIVE_INT_PATTERN.test(val)) return "Negative numbers are not allowed";
  return null;
}

const FIELD_CONFIG = {
  name:      { maxLength: 100, type: "text", pattern: /^[a-zA-Z0-9]*$/ },
  email:     { maxLength: 100, type: "text", validate: (val) => getEmailValidationError(val) },
  username:  { maxLength: 100, type: "text", validate: getUsernameValidationError },
  number:    { maxLength: 20,  type: "number" },
  slug:      { maxLength: 50,  type: "text", pattern: /^[a-z0-9-]*$/ },
  region:    { maxLength: 30,  type: "text", pattern: /^[a-z0-9-]*$/ },
  text:      { maxLength: 150, type: "text" },
  password:  { maxLength: 100, type: "password" },
  version:   { maxLength: 40,  type: "text", pattern: /^[a-z0-9-]*$/ },
  description: { maxLength: 1000, type: "text", pattern: /^[\s\S]*$/ },
};

export default function AppTextField({
  fieldType = "text",
  value = "",
  onChange,
  onErrorChange,
  errorOverride,
  helperTextOverride,
  size = "small",
  showCounter = true,
  reserveHelperSpace = true,
  maxLength,
  sx,
  ...props
}) {
  const config = FIELD_CONFIG[fieldType] || FIELD_CONFIG.text;
  const effectiveMaxLength = maxLength ?? config.maxLength;
  const externalError = Boolean(props.error);
  const resolvedType = props.type || config.type;
  const charCount = String(value ?? "").length;
  const isOverLimit = charCount > effectiveMaxLength;
  const counterText = isOverLimit
    ? `${charCount}/${effectiveMaxLength} exceeded`
    : `${charCount}/${effectiveMaxLength}`;

  const [error, setError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");

  const handleChange = (e) => {
    let val = e.target.value;
    if (fieldType === "email") {
      val = val.toLowerCase().replace(/[^a-z0-9@._+-]/g, "");
    }

    let hasError = false;
    let message = "";

    // Soft-limit validation: allow typing beyond limit, then show error.
    if (val.length > effectiveMaxLength) {
      hasError = true;
      message = "Character limit reached";
    } else if (config.validate) {
      const validateMessage = config.validate(val);
      if (validateMessage) {
        hasError = true;
        message = validateMessage;
      }
    } else if (config.pattern && !config.pattern.test(val)) {
      hasError = true;
      message = "Invalid format";
    }

    setError(hasError);
    setErrorMsg(message);

    // 👇 notify parent
    onErrorChange && onErrorChange(hasError, message);

    if (fieldType === "email" && val !== e.target.value) {
      onChange && onChange({ ...e, target: { ...e.target, value: val } });
      return;
    }

    onChange && onChange(e);
  };

  return (
    (() => {
      const effectiveError = typeof errorOverride === "boolean" ? errorOverride : error;
      const effectiveHelperText = effectiveError
        ? (helperTextOverride ?? (error ? errorMsg : ""))
        : (props.helperText ?? (reserveHelperSpace ? " " : null));

  return (
    <TextField
      {...props}
      value={value}
      type={resolvedType}
      size={size}
      error={effectiveError}
      sx={[
        showCounter && {
          "& .MuiFormHelperText-root": {
            marginLeft: 0,
            marginRight: 0,
            width: "100%",
          },
        },
        sx,
      ].filter(Boolean)}
      inputProps={{
        ...(props.inputProps || {}),
      }}
      helperText={showCounter ? (
        <span
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            gap: 8,
          }}
        >
          <span
            style={{
              opacity: 0.8,
              color: isOverLimit ? "#d32f2f" : "inherit",
              flexShrink: 0,
            }}
          >
            {counterText}
          </span>
          <span
            style={{
              color: effectiveError ? "#d32f2f" : "#707070",
              display: "inline-block",
              minHeight: "18px",
              textAlign: "right",
              flex: 1,
            }}
          >
            {effectiveHelperText}
          </span>
        </span>
      ) : (
        effectiveError
          ? (helperTextOverride ?? (error ? errorMsg : ""))
          : (props.helperText ?? (reserveHelperSpace ? " " : null))
      )}
      onChange={handleChange}
    />
      );
    })()
  );
}